import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Settings, FileText, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Module structure (same as backend)
const PRESENTATION_MODULES = {
  "Ginecología y Obstetricia": { hasSubmodules: false, submodules: [] },
  "Pediatría": { hasSubmodules: false, submodules: [] },
  "Cirugía": {
    hasSubmodules: true,
    submodules: [
      "Angiología", "Cirugía General", "Cirugía Maxilofacial", "Cirugía Plástica",
      "Neurocirugía", "Oftalmología", "Otorrinolaringología", "Trasplantes",
      "Traumatología y Ortopedia", "Urología"
    ]
  },
  "Medicina Interna": {
    hasSubmodules: true,
    submodules: [
      "Cardiología", "Dermatología", "Endocrinología", "Gastroenterología",
      "Hematología", "Infectología", "Medicina Interna (general)", "Nefrología",
      "Neumología", "Neurología", "Oncología", "Reumatología"
    ]
  },
  "Otros": {
    hasSubmodules: true,
    submodules: [
      "Alergia e Inmunología", "Analgesia y Anestesia", "Enfermedades Lisosomales",
      "Genética", "Geriatría", "Medicina Familiar", "Medicina Física y Rehabilitación",
      "Nutriología", "Psiquiatría", "Urgencias Médico-Quirúrgicas"
    ]
  }
};

const MODULE_ORDER = [
  "Ginecología y Obstetricia",
  "Cirugía",
  "Pediatría",
  "Medicina Interna",
  "Otros"
];

const PresentacionesDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { module, submodule } = useParams();
  const decodedModule = module ? decodeURIComponent(module) : null;
  const decodedSubmodule = submodule ? decodeURIComponent(submodule) : null;
  
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Determine current view
  const isModuleList = !decodedModule;
  const isSubmoduleList = decodedModule && PRESENTATION_MODULES[decodedModule]?.hasSubmodules && !decodedSubmodule;
  const isPresentationList = decodedModule && (!PRESENTATION_MODULES[decodedModule]?.hasSubmodules || decodedSubmodule);

  useEffect(() => {
    if (isPresentationList) {
      fetchPresentations();
    }
  }, [decodedModule, decodedSubmodule]);

  const fetchPresentations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (decodedModule) params.append('module', decodedModule);
      if (decodedSubmodule) params.append('submodule', decodedSubmodule);
      
      const response = await apiClient.get(`/presentations/list?${params.toString()}`);
      setPresentations(response.data);
    } catch (error) {
      toast.error('Error al cargar las presentaciones');
      console.error('Error fetching presentations:', error);
    } finally {
      setLoading(false);
    }
  };

  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleModuleClick = (moduleName) => {
    playPencilSound();
    setTimeout(() => {
      const moduleInfo = PRESENTATION_MODULES[moduleName];
      if (moduleInfo.hasSubmodules) {
        navigate(`/presentaciones/${encodeURIComponent(moduleName)}`);
      } else {
        navigate(`/presentaciones/${encodeURIComponent(moduleName)}/list`);
      }
    }, 100);
  };

  const handleSubmoduleClick = (submoduleName) => {
    playPencilSound();
    setTimeout(() => {
      navigate(`/presentaciones/${encodeURIComponent(decodedModule)}/${encodeURIComponent(submoduleName)}`);
    }, 100);
  };

  const handlePresentationClick = (presentationId) => {
    playPencilSound();
    setTimeout(() => {
      navigate(`/presentaciones/view/${presentationId}`);
    }, 100);
  };

  const handleBack = () => {
    if (isPresentationList && decodedSubmodule) {
      navigate(`/presentaciones/${encodeURIComponent(decodedModule)}`);
    } else if (isSubmoduleList || (isPresentationList && !decodedSubmodule)) {
      navigate('/presentaciones');
    } else {
      navigate('/');
    }
  };

  const getTitle = () => {
    if (isModuleList) return 'Presentaciones de Repaso';
    if (isSubmoduleList) return decodedModule;
    if (isPresentationList) return decodedSubmodule || decodedModule;
    return 'Presentaciones';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>{isModuleList ? 'Menú Principal' : 'Volver'}</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-12 text-center">
          {getTitle()}
        </h1>

        {/* Module List View */}
        {isModuleList && (
          <nav className="text-center">
            <ul className="space-y-6 sm:space-y-8">
              {MODULE_ORDER.map((moduleName, index) => (
                <li 
                  key={moduleName}
                  className="transform transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <button
                    onClick={() => handleModuleClick(moduleName)}
                    onMouseEnter={() => setHoveredItem(moduleName)}
                    onMouseLeave={() => setHoveredItem(null)}
                    data-testid={`module-${moduleName.toLowerCase().replace(/\s+/g, '-')}`}
                    className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-gray-700 hover:text-black transition-colors duration-300"
                  >
                    <span 
                      className={`relative w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-gray-400 transition-all duration-300 flex-shrink-0 ${
                        hoveredItem === moduleName ? 'border-black' : ''
                      }`}
                    >
                      <span 
                        className={`absolute inset-0.5 rounded-full bg-black transition-all duration-300 ${
                          hoveredItem === moduleName ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                        }`}
                      ></span>
                    </span>
                    <span className="transition-all duration-300 group-hover:tracking-wide">
                      {moduleName}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Submodule List View */}
        {isSubmoduleList && (
          <nav className="text-center">
            <ul className="space-y-6 sm:space-y-8">
              {PRESENTATION_MODULES[decodedModule].submodules.map((submoduleName, index) => (
                <li 
                  key={submoduleName}
                  className="transform transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <button
                    onClick={() => handleSubmoduleClick(submoduleName)}
                    onMouseEnter={() => setHoveredItem(submoduleName)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-gray-700 hover:text-black transition-colors duration-300"
                  >
                    <span 
                      className={`relative w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-gray-400 transition-all duration-300 flex-shrink-0 ${
                        hoveredItem === submoduleName ? 'border-black' : ''
                      }`}
                    >
                      <span 
                        className={`absolute inset-0.5 rounded-full bg-black transition-all duration-300 ${
                          hoveredItem === submoduleName ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                        }`}
                      ></span>
                    </span>
                    <span className="transition-all duration-300 group-hover:tracking-wide">
                      {submoduleName}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Presentation List View */}
        {isPresentationList && (
          <div className="w-full max-w-2xl px-4">
            {presentations.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">No hay presentaciones disponibles</p>
                <p className="text-sm">en esta sección aún.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {presentations.map((presentation, index) => (
                  <li 
                    key={presentation.id}
                    className="transform transition-all duration-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <button
                      onClick={() => handlePresentationClick(presentation.id)}
                      onMouseEnter={() => setHoveredItem(presentation.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left flex items-start space-x-3 ${
                        hoveredItem === presentation.id 
                          ? 'border-black bg-white shadow-md' 
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      {/* Circle indicator like GPCs */}
                      <div className="flex-shrink-0 mt-1">
                        <span 
                          className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            hoveredItem === presentation.id ? 'border-black' : 'border-gray-400'
                          }`}
                        >
                          <span 
                            className={`absolute w-3 h-3 rounded-full bg-black transition-all duration-200 ${
                              hoveredItem === presentation.id ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          ></span>
                        </span>
                      </div>
                      
                      {/* Content - NO truncate, show full title */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                          hoveredItem === presentation.id ? 'text-black' : 'text-gray-800'
                        }`}>
                          {presentation.title}
                        </h3>
                        {presentation.description && (
                          <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-snug">
                            {presentation.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight 
                        size={20} 
                        className={`flex-shrink-0 self-center transition-all duration-200 ${
                          hoveredItem === presentation.id ? 'text-black translate-x-1' : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PresentacionesDashboard;
